import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, ShoppingBag, ShieldCheck, Truck, BadgeCheck, ChevronRight, Minus, Plus } from "lucide-react";
import { api } from "../lib/api";
import { CardArt } from "../components/CardArt";
import { useCart, useWishlist } from "../lib/store";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => (!url ? null : url.startsWith("http") ? url : `${BACKEND}${url}`);

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const { toggle, has } = useWishlist();

  useEffect(() => {
    setP(null);
    api.get(`/products/${slug}`).then(({ data }) => setP(data));
  }, [slug]);

  if (!p) return <div className="max-w-7xl mx-auto px-4 py-20 text-[#9CA3AF]">Caricamento…</div>;

  const soldOut = p.stock === 0 && !p.is_preorder;
  const badges = [...(p.badges || [])];
  if (soldOut) badges.push("SOLD OUT");

  return (
    <div className="max-w-7xl mx-auto px-4 py-10 md:py-14 text-white">
      <div className="text-xs font-mono-ps text-[#9CA3AF] uppercase tracking-widest mb-6 flex items-center gap-2">
        <Link to="/" className="hover:text-[#C9A227]">Home</Link>
        <ChevronRight size={12} />
        <Link to={`/${p.category}`} className="hover:text-[#C9A227]">{p.category}</Link>
        <ChevronRight size={12} />
        <span className="text-white">{p.set_code || p.name.slice(0, 20)}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="border border-[#C9A227]/25 relative">
            {p.image_url ? (
              <div className="w-full h-80 bg-[#0b0b0d] overflow-hidden">
                <img src={resolveImg(p.image_url)} alt={p.name} className="w-full h-full object-contain" />
              </div>
            ) : (
              <CardArt product={p} size="lg" />
            )}
            {badges.length > 0 && (
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {badges.map((b) => (
                  <span key={b} className="ps-badge text-[#C9A227] border-[#C9A227]">{b}</span>
                ))}
              </div>
            )}
          </div>
          {!p.image_url && (
            <div className="grid grid-cols-4 gap-2 mt-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="border border-[#C9A227]/15 hover:border-[#C9A227] transition-colors cursor-pointer">
                  <CardArt product={p} size="sm" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div className="eyebrow">{p.game.replace("-", " ").toUpperCase()} • {p.category.toUpperCase()}</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white mt-2 leading-tight">{p.name}</h1>

          <div className="flex items-center gap-4 mt-4">
            <div className="flex items-baseline gap-3">
              {p.old_price && <span className="text-lg text-[#9CA3AF] line-through">€ {p.old_price.toFixed(2)}</span>}
              <span className="font-mono-ps font-bold text-3xl text-[#C9A227]">€ {p.price.toFixed(2)}</span>
            </div>
            <div className="ml-auto text-xs font-mono-ps uppercase tracking-widest text-[#9CA3AF]">
              {p.is_preorder ? "Preorder" : p.stock > 0 ? `Disponibile • ${p.stock} pz` : "Esaurito"}
            </div>
          </div>

          {p.preorder_type === "allocation-deposit" && p.deposit_percent && (
            <div className="mt-5 border border-[#C9A227] bg-[#C9A227]/8 p-4 rounded-sm">
              <div className="font-mono-ps text-[#C9A227] text-xs tracking-widest uppercase mb-2">Prodotto su allocazione</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF]">Acconto oggi ({p.deposit_percent}%)</div>
                  <div className="font-mono-ps font-bold text-xl text-white mt-1">€ {((p.price * p.deposit_percent) / 100).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF]">Saldo alla conferma</div>
                  <div className="font-mono-ps font-bold text-xl text-white mt-1">€ {((p.price * (100 - p.deposit_percent)) / 100).toFixed(2)}</div>
                </div>
              </div>
              <p className="text-xs text-[#D1D5DB] mt-3 leading-relaxed">
                Al preorder versi solo l'acconto. Il saldo verrà richiesto quando il distributore ci confermerà le quantità assegnate.
              </p>
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3">
            <Info label="Lingua" value={p.language?.toUpperCase() || "—"} />
            <Info label="Set" value={p.set_code || "—"} />
            <Info label="Codice" value={p.product_code || "—"} />
            <Info label="Condizione" value={p.condition.replace("-", " ")} />
            {p.delivery_time && <Info label="Tempi consegna" value={p.delivery_time} highlight />}
            {p.is_preorder && p.release_date && <Info label="Data uscita" value={p.release_date} highlight />}
          </div>

          {p.is_preorder && p.preorder_type !== "allocation-deposit" && (
            <div className="mt-5 border-l-2 border-[#C9A227] bg-[#C9A227]/5 p-4 text-sm text-[#D1D5DB]">
              <div className="font-mono-ps text-[#C9A227] text-xs tracking-widest uppercase mb-1">Preorder giapponese</div>
              Pagamento intero al preorder. {p.delivery_time ? `Arrivo previsto in ${p.delivery_time.toLowerCase()} dopo l'uscita ufficiale del ${p.release_date || ""}.` : "Spedizione al day-one."}
            </div>
          )}

          <p className="text-[#D1D5DB] mt-6 leading-relaxed">{p.description}</p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <div className="flex items-center border border-[#C9A227]/30 rounded-sm">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-white hover:text-[#C9A227]"><Minus size={16} /></button>
              <span className="w-12 text-center font-mono-ps text-white">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="p-3 text-white hover:text-[#C9A227]"><Plus size={16} /></button>
            </div>
            <button
              data-testid="pdp-add-to-cart"
              disabled={soldOut}
              onClick={() => { add(p, qty); toast.success("Aggiunto al carrello"); }}
              className="btn-gold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ShoppingBag size={16} /> {soldOut ? "Esaurito" : "Aggiungi al carrello"}
            </button>
            <button
              data-testid="pdp-wishlist"
              onClick={() => { toggle(p.product_id); toast(has(p.product_id) ? "Rimosso dalla wishlist" : "Aggiunto alla wishlist"); }}
              className="btn-ghost-gold"
            >
              <Heart size={16} className={has(p.product_id) ? "fill-[#C9A227]" : ""} /> Wishlist
            </button>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Assurance icon={<Truck size={16} />} label="Spedizione sicura" desc="Bolla d'aria + toploader" />
            <Assurance icon={<ShieldCheck size={16} />} label="Pagamenti sicuri" desc="Stripe / PayPal / Apple Pay" />
            <Assurance icon={<BadgeCheck size={16} />} label="Prodotti autentici" desc="100% originali" />
          </div>
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value, highlight }) => (
  <div className={`border ${highlight ? "border-[#C9A227]" : "border-[#C9A227]/15"} rounded-sm p-3 bg-[#0b0b0d]`}>
    <div className="text-[10px] font-mono-ps tracking-widest uppercase text-[#9CA3AF]">{label}</div>
    <div className={`font-mono-ps mt-1 ${highlight ? "text-[#C9A227]" : "text-white"}`}>{value}</div>
  </div>
);

const Assurance = ({ icon, label, desc }) => (
  <div className="border border-[#C9A227]/15 rounded-sm p-3 bg-[#0b0b0d]">
    <div className="flex items-center gap-2 text-[#C9A227]">{icon}<span className="text-xs font-mono-ps uppercase tracking-widest">{label}</span></div>
    <div className="text-xs text-[#9CA3AF] mt-1">{desc}</div>
  </div>
);
